import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Building2,
  Search, Filter, Eye, MessageCircle, FileText,
  Package, RefreshCw, X,
  CheckCircle, Clock, Truck, AlertCircle, Edit,
  Upload, Download, Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import AlbaranMixtoCrearModal from '../components/AlbaranMixtoCrearModal';

const API_URL = process.env.REACT_APP_API_URL;

export default function ClientManagementProveedor() {
  const { user } = useContext(AuthContext);
  const [proveedorInfo, setProveedorInfo] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Estados para clientes asociados
  const [clientesAsociados, setClientesAsociados] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [loadingClientes, setLoadingClientes] = useState(false);
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    npedido: '',
    fecha: '',
    estado: '',
    cliente: ''
  });
  
  // Estados para comentarios
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [comentarios, setComentarios] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  
  // Estados para modal de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  
  // Estados para acciones masivas
  const [selectedPedidos, setSelectedPedidos] = useState([]);
  const [estadoMasivo, setEstadoMasivo] = useState('');
  
  // Valor del input de albarán por pedido (se rellena con defaultValue al render).
  // No usamos `editingAlbaran` ni toggle de edición: el input está siempre
  // editable y se guarda al perder foco (onBlur) o pulsar Enter.
  const [albaranValues, setAlbaranValues] = useState({});

  // Modal de creación de albarán pendiente (para clientes en modo 'albaran' o 'mixto'):
  // tras guardar el número de albarán, si el cliente gestiona stock, abrimos
  // el modal con tabla de líneas (cantidad/lote/fecha por línea, todo opcional
  // salvo cantidad).
  const [mixtoModal, setMixtoModal] = useState({ open: false, pedidoId: null, albaranNumero: '' });

  // Estados para facturas
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedPedidoForInvoice, setSelectedPedidoForInvoice] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Modal de envío parcial
  const [showParcialModal, setShowParcialModal] = useState(false);
  const [parcialPedidoId, setParcialPedidoId] = useState(null);
  const [parcialLineas, setParcialLineas] = useState([]);
  const [parcialFaltantes, setParcialFaltantes] = useState({});
  const [parcialLoading, setParcialLoading] = useState(false);

  // Efecto para scrollear al inicio cuando el componente se monta
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Cargar información del proveedor
  const loadProveedorData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/proveedores/mi-info`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProveedorInfo(data.proveedor);
      } else {
        console.error('Error loading proveedor info:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error al cargar información del proveedor:', error);
    }
  }, []);

  // Cargar clientes asociados al proveedor
  const loadClientesAsociados = useCallback(async () => {
    try {
      setLoadingClientes(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/proveedores/mis-clientes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientesAsociados(data.clientes || []);
      } else {
        console.error('Error loading clientes asociados:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error al cargar clientes asociados:', error);
    } finally {
      setLoadingClientes(false);
    }
  }, []);

  // Cargar pedidos del proveedor
  const loadPedidos = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filtros.npedido) params.append('npedido', filtros.npedido);
      if (filtros.fecha) params.append('fecha', filtros.fecha);
      if (filtros.estado) params.append('estado', filtros.estado);
      if (filtros.cliente) params.append('cliente', filtros.cliente);
      
      if (clienteSeleccionado && !filtros.cliente) {
        params.append('cliente', clienteSeleccionado.razon_social);
      }
      
      const response = await fetch(`${API_URL}/api/proveedores/mis-pedidos?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPedidos(data.pedidos || []);
        
        const initialAlbaranValues = {};
        data.pedidos.forEach(pedido => {
          initialAlbaranValues[pedido.id] = pedido.albaran || '';
        });
        setAlbaranValues(initialAlbaranValues);
      } else {
        console.error('Error response:', response.status, response.statusText);
        setError('Error al cargar pedidos');
      }
    } catch (error) {
      console.error('Error al cargar pedidos:', error);
      setError('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  }, [filtros, clienteSeleccionado]);

  // Cargar datos iniciales
  useEffect(() => {
    if (user?.id) {
      loadProveedorData();
      loadClientesAsociados();
      loadPedidos();
    }
  }, [user, loadProveedorData, loadClientesAsociados, loadPedidos]);

  // Recargar pedidos cuando cambien los filtros o el cliente seleccionado
  useEffect(() => {
    if (user?.id && 
        filtros.npedido === '' && 
        filtros.fecha === '' && 
        filtros.estado === '' && 
        filtros.cliente === '') {
      const timer = setTimeout(() => {
        loadPedidos();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [filtros, loadPedidos, user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadPedidos();
    }
  }, [clienteSeleccionado, loadPedidos, user?.id]);

  // Aplicar filtros
  const applyFilters = useCallback(() => {
    loadPedidos();
  }, [loadPedidos]);

  // Limpiar filtros
  const clearFilters = useCallback(() => {
    setFiltros({
      npedido: '',
      fecha: '',
      estado: '',
      cliente: ''
    });
    setClienteSeleccionado(null);
  }, []);

  // Manejar cambios en filtros
  const handleFilterChange = (field, value) => {
    setFiltros(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'cliente' && value) {
      setClienteSeleccionado(null);
    }
  };

  // Funciones para manejar selección de clientes
  const handleSelectCliente = (cliente) => {
    setClienteSeleccionado(cliente);
    setFiltros(prev => ({
      ...prev,
      cliente: ''
    }));
  };

  const handleDeselectCliente = () => {
    setClienteSeleccionado(null);
  };

  // Funciones para comentarios
  const openCommentModal = async (pedido) => {
    setSelectedPedido(pedido);
    setShowCommentModal(true);
    await fetchComentarios(pedido.id);
  };

  const fetchComentarios = async (pedidoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}/comentarios-pedido`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setComentarios(data.comentarios || []);
      }
    } catch (error) {
      console.error('Error al cargar comentarios:', error);
    }
  };

  const addComentario = async () => {
    if (!nuevoComentario.trim() || !selectedPedido) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${selectedPedido.id}/comentarios-pedido`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ texto: nuevoComentario })
      });
      
      if (response.ok) {
        setNuevoComentario('');
        await fetchComentarios(selectedPedido.id);
      }
    } catch (error) {
      console.error('Error al agregar comentario:', error);
    }
  };

  // Funciones para acciones masivas
  const handleSelectPedido = (pedidoId, isSelected) => {
    if (isSelected) {
      setSelectedPedidos(prev => [...prev, pedidoId]);
    } else {
      setSelectedPedidos(prev => prev.filter(id => id !== pedidoId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedPedidos(pedidos.map(p => p.id));
    } else {
      setSelectedPedidos([]);
    }
  };

  const updateEstadoMasivo = async () => {
    if (selectedPedidos.length === 0 || !estadoMasivo) {
      alert('Selecciona pedidos y un estado');
      return;
    }

    // Envío parcial requiere especificar qué líneas faltan → abrir modal
    if (estadoMasivo === '4') {
      if (selectedPedidos.length !== 1) {
        alert('Para marcar un envío parcial, seleccioná un único pedido a la vez para indicar las líneas faltantes.');
        return;
      }
      await openParcialModal(selectedPedidos[0]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/actualizar-estados`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pedidos_ids: selectedPedidos,
          estado: estadoMasivo
        })
      });

      if (response.ok) {
        alert('Estados actualizados correctamente');
        setSelectedPedidos([]);
        setEstadoMasivo('');
        loadPedidos();
      }
    } catch (error) {
      console.error('Error al actualizar estados:', error);
      alert('Error al actualizar estados');
    }
  };

  // Abrir modal de envío parcial: carga las líneas del pedido
  const openParcialModal = async (pedidoId) => {
    try {
      setParcialLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        alert('No se pudieron cargar las líneas del pedido');
        setParcialLoading(false);
        return;
      }
      const data = await response.json();
      const lineas = data.articulos || [];
      setParcialPedidoId(pedidoId);
      setParcialLineas(lineas);
      const initial = {};
      lineas.forEach(l => { initial[l.id] = l.cantidad_faltante || 0; });
      setParcialFaltantes(initial);
      setShowParcialModal(true);
    } catch (error) {
      console.error('Error al cargar líneas:', error);
      alert('Error al cargar líneas del pedido');
    } finally {
      setParcialLoading(false);
    }
  };

  const confirmParcial = async () => {
    // Validar
    for (const linea of parcialLineas) {
      const f = Number(parcialFaltantes[linea.id] || 0);
      if (!Number.isInteger(f) || f < 0 || f > linea.cantidad) {
        alert(`Línea "${linea.nombre}": valor inválido (debe ser entre 0 y ${linea.cantidad}).`);
        return;
      }
    }
    const totalFaltante = parcialLineas.reduce((s, l) => s + Number(parcialFaltantes[l.id] || 0), 0);
    if (totalFaltante === 0) {
      alert('No marcaste ninguna cantidad faltante. Si enviaste todo, marcá el pedido como "Enviado" en lugar de "Envío parcial".');
      return;
    }

    try {
      setParcialLoading(true);
      const token = localStorage.getItem('token');

      // Persistir cantidad_faltante línea por línea
      for (const linea of parcialLineas) {
        const f = Number(parcialFaltantes[linea.id] || 0);
        const resp = await fetch(`${API_URL}/api/pedidos/${parcialPedidoId}/articulos/${linea.id}/faltante`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ cantidad_faltante: f })
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.message || 'Error al guardar cantidad faltante');
        }
      }

      // Actualizar estado del pedido a "Envío parcial"
      const respEstado = await fetch(`${API_URL}/api/pedidos/actualizar-estados`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pedidos_ids: [parcialPedidoId], estado: '4' })
      });
      if (!respEstado.ok) {
        throw new Error('Error al actualizar estado del pedido');
      }

      alert('Envío parcial registrado correctamente');
      setShowParcialModal(false);
      setParcialPedidoId(null);
      setParcialLineas([]);
      setParcialFaltantes({});
      setSelectedPedidos([]);
      setEstadoMasivo('');
      loadPedidos();
    } catch (error) {
      console.error('Error en envío parcial:', error);
      alert('Error: ' + error.message);
    } finally {
      setParcialLoading(false);
    }
  };

  // Función para mostrar confirmación
  const showConfirmation = (message, action) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  // Función para confirmar acción
  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction();
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmMessage('');
  };

  const eliminarPedidos = async () => {
    if (selectedPedidos.length === 0) {
      alert('Selecciona al menos un pedido');
      return;
    }

    const actualEliminar = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/pedidos/eliminar`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ pedidos_ids: selectedPedidos })
        });
        
        if (response.ok) {
          alert('Pedidos eliminados correctamente');
          setSelectedPedidos([]);
          loadPedidos();
        }
      } catch (error) {
        console.error('Error al eliminar pedidos:', error);
        alert('Error al eliminar pedidos');
      }
    };

    showConfirmation(
      `¿Estás seguro de que quieres eliminar ${selectedPedidos.length} pedido(s)? Esta acción no se puede deshacer.`,
      actualEliminar
    );
  };

  const handleSaveAlbaran = async (pedidoId) => {
    try {
      const token = localStorage.getItem('token');
      const albaranValue = albaranValues[pedidoId];

      // 1) Guardar el número de albarán en `pedidos.albaran` (siempre)
      const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}/albaran`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ albaran: albaranValue })
      });
      if (!response.ok) {
        throw new Error('Error al guardar el número de albarán');
      }

      // 2) Si el cliente del pedido gestiona stock por albarán o mixto,
      //    abrimos el modal de creación de albarán pendiente con sus líneas.
      //    Si no, solo dejamos guardado el número (no se gestiona stock).
      if (albaranValue && albaranValue.trim()) {
        try {
          const previewResp = await fetch(`${API_URL}/api/stock/pedido/${pedidoId}/albaran-preview`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const previewData = await previewResp.json();
          if (previewData.modo_stock === 'albaran' || previewData.modo_stock === 'mixto') {
            setMixtoModal({ open: true, pedidoId, albaranNumero: albaranValue });
            // No recargamos la lista todavía — la recargaremos al cerrar el modal
            return;
          }
        } catch (e) {
          console.error('Stock albaran flow fallo:', e);
        }
      }

      loadPedidos();
    } catch (error) {
      console.error('Error al actualizar albarán:', error);
    }
  };

  // Función para descargar PDF
  const handleDescargarPDF = async (pedidoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/pedidos/${pedidoId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error("Error al descargar el PDF");
      }
      
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `pedido_${pedidoId}.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1];
        }
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error("Error:", error);
      alert("Error al descargar el PDF");
    }
  };

  // Funciones para facturas
  const openInvoiceModal = (pedido) => {
    // Si ya existe una factura, permitir modificarla sin importar el estado
    if (!pedido.pdf_factura) {
      // Solo validar estado si NO existe factura
      if (![3, 4, 5].includes(pedido.estado)) {
        alert('Solo se pueden subir facturas a pedidos con estado Enviado, Envío parcial o Cerrado');
        return;
      }
    }
    
    setSelectedPedidoForInvoice(pedido);
    setShowInvoiceModal(true);
    setInvoiceFile(null);
  };

  const handleInvoiceFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Solo se permiten archivos PDF');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        alert('El archivo no puede superar los 50MB');
        return;
      }
      setInvoiceFile(file);
    }
  };

  const uploadInvoice = async () => {
    if (!invoiceFile || !selectedPedidoForInvoice) return;

    try {
      setUploadingInvoice(true);
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', invoiceFile);

      const response = await fetch(`${API_URL}/api/invoicing/pedidos/${selectedPedidoForInvoice.id}/upload-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const isModifying = selectedPedidoForInvoice.pdf_factura;
        alert(isModifying ? 'Factura modificada correctamente' : 'Factura subida correctamente');
        setShowInvoiceModal(false);
        setInvoiceFile(null);
        setSelectedPedidoForInvoice(null);
        loadPedidos();
      } else {
        const data = await response.json();
        alert(data.message || 'Error al subir la factura');
      }
    } catch (error) {
      console.error('Error al subir factura:', error);
      alert('Error al subir la factura');
    } finally {
      setUploadingInvoice(false);
    }
  };

  const viewInvoice = async (pedidoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoicing/pedidos/${pedidoId}/invoice`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.pdf_url, '_blank');
      } else {
        alert('No hay factura disponible para este pedido');
      }
    } catch (error) {
      console.error('Error al ver factura:', error);
      alert('Error al ver la factura');
    }
  };

  const deleteInvoice = async (pedidoId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta factura?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoicing/pedidos/${pedidoId}/invoice`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Factura eliminada correctamente');
        loadPedidos();
      } else {
        const data = await response.json();
        alert(data.message || 'Error al eliminar la factura');
      }
    } catch (error) {
      console.error('Error al eliminar factura:', error);
      alert('Error al eliminar la factura');
    }
  };

  // Función para obtener el icono del estado
  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 1: return <Clock className="w-4 h-4 text-yellow-500" />;
      case 2: return <RefreshCw className="w-4 h-4 text-blue-500" />;
      case 3: return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 4: return <Truck className="w-4 h-4 text-orange-500" />;
      case 5: return <CheckCircle className="w-4 h-4 text-gray-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  // Función para obtener el texto del estado
  const getEstadoText = (estado) => {
    switch (estado) {
      case 1: return 'Abierto';
      case 2: return 'En proceso';
      case 3: return 'Enviado';
      case 4: return 'Envío parcial';
      case 5: return 'Cerrado';
      default: return 'Desconocido';
    }
  };

  if (loading && !pedidos.length) {
    return (
      <div className="fixed inset-0 top-16 bg-white flex justify-center items-center z-50">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-20 h-20 border-4 rounded-full animate-spin border-t-teal-600 mx-auto" style={{borderColor: '#3cbf9a40', borderTopColor: '#3cbf9a'}}></div>
            <Building2 className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" style={{color: '#3cbf9a'}} />
          </div>
          <h3 className="text-xl font-bold mb-2" style={{color: '#3cbf9a'}}>Cargando pedidos</h3>
          <p className="text-gray-600">Preparando dashboard del proveedor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 top-16 bg-gradient-to-br from-gray-50 to-gray-100 flex">
      
      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          <div className="max-w-full mx-auto space-y-6">
            {/* Header */}
            <div className="rounded-xl shadow-lg p-6" style={{ backgroundColor: '#3cbf9a', color: 'white' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white">Gestión de Pedidos</h1>
                  <p className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {proveedorInfo?.nombre || 'Panel de Proveedor'}
                    {clienteSeleccionado && (
                      <span className="text-sm block mt-1" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        📋 Filtrado por: {clienteSeleccionado.razon_social}
                      </span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{pedidos.length}</div>
                  <div className="text-sm opacity-90">
                    {clienteSeleccionado ? 'Pedidos del cliente' : 'Pedidos totales'}
                  </div>
                </div>
              </div>
              
              {clienteSeleccionado && (
                <div className="mt-4 flex items-center justify-between bg-white bg-opacity-20 rounded-lg p-3">
                  <div className="flex items-center">
                    <Building2 size={16} className="mr-2" />
                    <span className="text-sm">
                      Mostrando pedidos de: <strong>{clienteSeleccionado.razon_social}</strong>
                    </span>
                  </div>
                  <button
                    onClick={handleDeselectCliente}
                    className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition-colors duration-200"
                    title="Ver todos los pedidos"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Filtros */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Filter size={20} className="mr-2" style={{ color: '#3cbf9a' }} />
                  Filtros de Búsqueda
                </h3>
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                >
                  <X size={16} className="mr-1" />
                  Limpiar
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Pedido
                  </label>
                  <input
                    type="text"
                    value={filtros.npedido}
                    onChange={(e) => handleFilterChange('npedido', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#3cbf9a' }}
                    placeholder="Ej: PED001"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cliente
                  </label>
                  <input
                    type="text"
                    value={filtros.cliente}
                    onChange={(e) => handleFilterChange('cliente', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#3cbf9a' }}
                    placeholder="Nombre del cliente"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha
                  </label>
                  <input
                    type="date"
                    value={filtros.fecha}
                    onChange={(e) => handleFilterChange('fecha', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#3cbf9a' }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <select
                    value={filtros.estado}
                    onChange={(e) => handleFilterChange('estado', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#3cbf9a' }}
                  >
                    <option value="">Todos los estados</option>
                    <option value="2">En proceso</option>
                    <option value="3">Enviado</option>
                    <option value="4">Envío parcial</option>
                    <option value="5">Cerrado</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={applyFilters}
                    className="w-full text-white py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                    style={{ backgroundColor: '#3cbf9a' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2da080'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#3cbf9a'}
                  >
                    <Search size={18} className="mr-2" />
                    Buscar
                  </button>
                </div>
              </div>
            </div>

            {/* Acciones masivas */}
            {selectedPedidos.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-700">
                      {selectedPedidos.length} pedido(s) seleccionado(s)
                    </span>
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="estado_masivo"
                          value="3"
                          checked={estadoMasivo === '3'}
                          onChange={(e) => setEstadoMasivo(e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm">Enviado</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="estado_masivo"
                          value="4"
                          checked={estadoMasivo === '4'}
                          onChange={(e) => setEstadoMasivo(e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm">Envío Parcial</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={updateEstadoMasivo}
                      disabled={!estadoMasivo}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200"
                    >
                      Actualizar Estados
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tabla de pedidos */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Package size={20} className="mr-2" style={{ color: '#3cbf9a' }} />
                  Pedidos Recibidos
                  <span className="ml-auto text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {pedidos.length} pedidos
                  </span>
                </h3>
              </div>
              
              {pedidos.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead style={{ backgroundColor: '#3cbf9a' }}>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Seleccionar
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Estado</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Razón Social</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Comentarios</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Total IVA</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Albarán</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Factura</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pedidos.map((pedido) => (
                        <tr key={pedido.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedPedidos.includes(pedido.id)}
                              onChange={(e) => handleSelectPedido(pedido.id, e.target.checked)}
                              className="rounded"
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {pedido.id}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getEstadoIcon(pedido.estado)}
                              <span className="ml-2 text-sm text-gray-900">
                                {getEstadoText(pedido.estado)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {pedido.razon_social}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(pedido.fecha).toLocaleDateString('es-ES')}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 max-w-xs truncate">
                            {pedido.comentarios || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {pedido.itotaliva ? `€${parseFloat(pedido.itotaliva).toFixed(2)}` : 'N/A'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {/* Input siempre editable. Al perder foco se dispara handleSaveAlbaran:
                                - PUT del número en pedidos.albaran (siempre)
                                - Si el cliente está en modo albaran/mixto → abre modal con líneas */}
                            <input
                              type="text"
                              defaultValue={pedido.albaran || ''}
                              placeholder="Sin albarán"
                              className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                              onChange={(e) => setAlbaranValues(prev => ({
                                ...prev,
                                [pedido.id]: e.target.value
                              }))}
                              onBlur={(e) => {
                                if (e.target.value !== (pedido.albaran || '')) {
                                  setAlbaranValues(prev => ({ ...prev, [pedido.id]: e.target.value }));
                                  handleSaveAlbaran(pedido.id);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-center space-x-2">
                              {pedido.pdf_factura ? (
                                // Ya existe factura - mostrar icono de ver y modificar
                                <>
                                  <button
                                    onClick={() => viewInvoice(pedido.id)}
                                    className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                                    title="Ver factura"
                                  >
                                    <FileText size={18} />
                                  </button>
                                  <button
                                    onClick={() => openInvoiceModal(pedido)}
                                    className="text-orange-600 hover:text-orange-900 transition-colors duration-200"
                                    title="Modificar factura"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={() => deleteInvoice(pedido.id)}
                                    className="text-red-600 hover:text-red-900 transition-colors duration-200"
                                    title="Eliminar factura"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              ) : (
                                // No existe factura - mostrar icono de subir solo si está en estado 3, 4 o 5
                                [3, 4, 5].includes(pedido.estado) && (
                                  <button
                                    onClick={() => openInvoiceModal(pedido)}
                                    className="text-green-600 hover:text-green-900 transition-colors duration-200"
                                    title="Subir factura"
                                  >
                                    <Upload size={18} />
                                  </button>
                                )
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => openCommentModal(pedido)}
                                className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                                title="Ver comentarios"
                              >
                                <MessageCircle size={16} />
                              </button>
                              <button
                                onClick={() => navigate(`/pedidos/detalles/${pedido.id}`)}
                                className="text-green-600 hover:text-green-900 transition-colors duration-200"
                                title="Ver detalles"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => handleDescargarPDF(pedido.id)}
                                className="text-red-600 hover:text-red-900 transition-colors duration-200"
                                title="Descargar PDF"
                              >
                                <FileText size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Package size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pedidos</h3>
                  <p className="text-gray-500">
                    No tienes pedidos registrados en este momento.
                  </p>
                </div>
              )}
            </div>

            {/* Modal de comentarios */}
            {showCommentModal && selectedPedido && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                  <div className="text-white p-4 border-b" style={{ backgroundColor: '#3cbf9a' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <MessageCircle size={20} className="mr-2" />
                        <div>
                          <h2 className="text-lg font-bold">Comentarios del Pedido</h2>
                          <p className="text-sm opacity-80">Pedido #{selectedPedido.id}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowCommentModal(false)}
                        className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>

                  <div className="p-4 flex flex-col h-96">
                    <div className="flex-1 overflow-y-auto mb-4 space-y-3">
                      {comentarios.length > 0 ? (
                        comentarios.map((comentario, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-lg max-w-[80%] ${
                              comentario.usuario_id === user?.id
                                ? 'bg-green-100 ml-auto'
                                : 'bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">
                                {comentario.username}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(comentario.fecha).toLocaleString('es-ES')}
                              </span>
                            </div>
                            <p className="text-sm">{comentario.texto}</p>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-gray-500 py-8">
                          No hay comentarios para este pedido
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <textarea
                        value={nuevoComentario}
                        onChange={(e) => setNuevoComentario(e.target.value)}
                        placeholder="Escribe un comentario..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#3cbf9a' }}
                        rows="2"
                      />
                      <button
                        onClick={addComentario}
                        disabled={!nuevoComentario.trim()}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200"
                      >
                        Enviar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de subida de factura */}
            {showInvoiceModal && selectedPedidoForInvoice && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                  <div className="text-white p-4 border-b" style={{ backgroundColor: '#3cbf9a' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Upload size={20} className="mr-2" />
                        <div>
                          <h2 className="text-lg font-bold">
                            {selectedPedidoForInvoice.pdf_factura ? 'Modificar Factura' : 'Subir Factura'}
                          </h2>
                          <p className="text-sm opacity-80">Pedido #{selectedPedidoForInvoice.id}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => {
                          setShowInvoiceModal(false);
                          setInvoiceFile(null);
                          setSelectedPedidoForInvoice(null);
                        }}
                        className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    {selectedPedidoForInvoice.pdf_factura && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start">
                          <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-yellow-800">
                            <p className="font-semibold mb-1">Advertencia</p>
                            <p>Ya existe una factura para este pedido. Al subir un nuevo archivo, se reemplazará la factura actual.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seleccionar archivo PDF
                      </label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleInvoiceFileChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': '#3cbf9a' }}
                      />
                      {invoiceFile && (
                        <p className="mt-2 text-sm text-gray-600 flex items-center">
                          <FileText size={16} className="mr-1" />
                          {invoiceFile.name}
                        </p>
                      )}
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setShowInvoiceModal(false);
                          setInvoiceFile(null);
                          setSelectedPedidoForInvoice(null);
                        }}
                        className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors duration-200"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={uploadInvoice}
                        disabled={!invoiceFile || uploadingInvoice}
                        className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 flex items-center justify-center"
                      >
                        {uploadingInvoice ? (
                          <>
                            <RefreshCw size={16} className="mr-2 animate-spin" />
                            Subiendo...
                          </>
                        ) : (
                          <>
                            <Upload size={16} className="mr-2" />
                            {selectedPedidoForInvoice.pdf_factura ? 'Reemplazar' : 'Subir'} Factura
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de confirmación */}
            {showConfirmModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                  <div className="p-6">
                    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                      <AlertCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                      Confirmar Acción
                    </h3>
                    <p className="text-gray-600 text-center mb-6">
                      {confirmMessage}
                    </p>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowConfirmModal(false)}
                        className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors duration-200"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleConfirm}
                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de envío parcial */}
            {showParcialModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                  <div className="p-6 border-b border-gray-200" style={{ backgroundColor: '#3cbf9a' }}>
                    <h3 className="text-lg font-bold text-white">Envío parcial — Pedido #{parcialPedidoId}</h3>
                    <p className="text-sm text-white opacity-90 mt-1">
                      Indicá cuántas unidades faltan de cada línea. Dejá en 0 las líneas que se enviaron completas.
                    </p>
                  </div>
                  <div className="p-6 overflow-y-auto flex-1">
                    {parcialLineas.length === 0 ? (
                      <p className="text-gray-500 text-center">No hay líneas en este pedido.</p>
                    ) : (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Producto</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Pedido</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Falta</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {parcialLineas.map(linea => (
                            <tr key={linea.id}>
                              <td className="px-3 py-2 text-sm text-gray-800">{linea.nombre}</td>
                              <td className="px-3 py-2 text-sm text-gray-800 text-right">{linea.cantidad}</td>
                              <td className="px-3 py-2 text-right">
                                <input
                                  type="number"
                                  min="0"
                                  max={linea.cantidad}
                                  value={parcialFaltantes[linea.id] ?? 0}
                                  onChange={(e) => setParcialFaltantes(prev => ({
                                    ...prev,
                                    [linea.id]: e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                                  }))}
                                  className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowParcialModal(false);
                        setParcialPedidoId(null);
                        setParcialLineas([]);
                        setParcialFaltantes({});
                      }}
                      disabled={parcialLoading}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={confirmParcial}
                      disabled={parcialLoading}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                    >
                      {parcialLoading ? 'Guardando...' : 'Confirmar envío parcial'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <AlbaranMixtoCrearModal
        open={mixtoModal.open}
        pedidoId={mixtoModal.pedidoId}
        albaranNumero={mixtoModal.albaranNumero}
        onClose={() => {
          setMixtoModal({ open: false, pedidoId: null, albaranNumero: '' });
          loadPedidos();
        }}
        onCreated={() => {
          setMixtoModal({ open: false, pedidoId: null, albaranNumero: '' });
          loadPedidos();
        }}
      />
    </div>
  );
}